import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTheme } from "../components/ThemeContaxt";

import RagOrHallucination from "../components/games/RagOrHallucination";
import DebugTheCode       from "../components/games/DebugTheCode";
import TechStackTrivia    from "../components/games/TechStackTrivia";
import TokenEstimator     from "../components/games/TokenEstimator";
import ApiStatusBlitz     from "../components/games/ApiStatusBlitz";

const GAMES = [
  {
    id: "rag",
    index: "01",
    title: "RAG or Hallucination",
    meta: "Real fact or AI lie?",
    detail: "8 rounds",
    difficulty: "Medium",
    component: RagOrHallucination,
    maxScore: 8,
  },
  {
    id: "debug",
    index: "02",
    title: "Debug the Code",
    meta: "Click the broken line.",
    detail: "5 snippets",
    difficulty: "Hard",
    component: DebugTheCode,
    maxScore: 5,
  },
  {
    id: "trivia",
    index: "03",
    title: "Tech Stack Trivia",
    meta: "AI, infra, and backend.",
    detail: "7 questions",
    difficulty: "Easy",
    component: TechStackTrivia,
    maxScore: 7,
  },
  {
    id: "tokens",
    index: "04",
    title: "Token Estimator",
    meta: "Guess the token count.",
    detail: "5 rounds",
    difficulty: "Medium",
    component: TokenEstimator,
    maxScore: 15,
  },
  {
    id: "status",
    index: "05",
    title: "API Status Blitz",
    meta: "Name that status code.",
    detail: "10 codes · 6s each",
    difficulty: "Hard",
    component: ApiStatusBlitz,
    maxScore: 10,
  },
  {
    id: "2048",
    index: "06",
    title: "2048",
    meta: "Merge tiles to reach 2048.",
    detail: "Classic",
    difficulty: "Easy",
    type: "iframe",
    url: "https://riishiiiii.github.io/2048/",
  },
];

const DIFF_COLOR = { Easy: "#22c55e", Medium: "#f59e0b", Hard: "#ef4444" };

/* ── Card ───────────────────────────────────────────────────────────── */
function GameCard({ game, onPlay, score, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      onClick={onPlay}
      whileHover="hovered"
      style={{ cursor: "pointer", height: "100%" }}
    >
      <motion.div
        variants={{
          hovered: { borderColor: "var(--border-strong)", y: -3 },
        }}
        transition={{ type: "spring", stiffness: 380, damping: 28 }}
        style={{
          height: "100%",
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top row: index + difficulty */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}>
          <span style={{
            fontFamily: "monospace",
            fontSize: "0.65rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            color: "var(--text-muted)",
            opacity: 0.5,
            userSelect: "none",
          }}>
            {game.index}
          </span>

          <span style={{
            fontSize: "0.65rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: DIFF_COLOR[game.difficulty],
            opacity: 0.85,
          }}>
            {game.difficulty}
          </span>
        </div>

        {/* Title */}
        <h3 style={{
          fontFamily: "'Space Grotesk Variable', system-ui, sans-serif",
          fontSize: "clamp(1.05rem, 2vw, 1.2rem)",
          fontWeight: 700,
          letterSpacing: "-0.025em",
          color: "var(--text)",
          margin: "0 0 8px",
          lineHeight: 1.15,
        }}>
          {game.title}
        </h3>

        {/* Meta */}
        <p style={{
          fontSize: "0.8rem",
          color: "var(--text-muted)",
          margin: 0,
          lineHeight: 1.5,
          flexGrow: 1,
        }}>
          {game.meta}
        </p>

        {/* Bottom row: rounds + score + play */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 24,
          paddingTop: 16,
          borderTop: "1px solid var(--border)",
        }}>
          <span style={{
            fontSize: "0.72rem",
            color: "var(--text-muted)",
            opacity: 0.7,
          }}>
            {!game.type && score !== undefined
              ? `${score} / ${game.maxScore} last time`
              : game.detail}
          </span>

          <motion.span
            variants={{ hovered: { x: 4 } }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            style={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "var(--text-muted)",
              letterSpacing: "0.01em",
            }}
          >
            Play →
          </motion.span>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Modal ──────────────────────────────────────────────────────────── */
function GameModal({ game, onClose, onFinish, finalScore }) {
  const isIframe = game.type === "iframe";
  const GameComponent = isIframe ? null : game.component;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isIframe ? "8px" : "16px",
        backdropFilter: "blur(4px)",
      }}
    >
      <motion.div
        initial={{ scale: 0.97, opacity: 0, y: 14 }}
        animate={{ scale: 1,    opacity: 1, y: 0  }}
        exit={{    scale: 0.98, opacity: 0         }}
        transition={{ type: "spring", stiffness: 340, damping: 32 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          border: "1px solid var(--border)",
          borderRadius: 20,
          width: isIframe ? "min(520px, 98vw)" : "min(900px, 96vw)",
          height: isIframe ? "min(920px, 96vh)" : undefined,
          maxHeight: isIframe ? "96vh" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isIframe ? "12px 20px" : "18px clamp(20px, 4vw, 40px)",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{
              fontFamily: "monospace",
              fontSize: "0.65rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: "var(--text-muted)",
              opacity: 0.5,
            }}>
              {game.index}
            </span>
            <h2 style={{
              margin: 0,
              fontFamily: "'Space Grotesk Variable', system-ui, sans-serif",
              fontSize: "clamp(0.95rem, 2vw, 1.1rem)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--text)",
            }}>
              {game.title}
            </h2>
            <span style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: DIFF_COLOR[game.difficulty],
              opacity: 0.8,
            }}>
              {game.difficulty}
            </span>
          </div>

          <motion.button
            whileHover={{ rotate: 90, scale: 1.1 }}
            transition={{ type: "spring", stiffness: 420, damping: 20 }}
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32, height: 32,
              borderRadius: "50%",
              border: "1px solid var(--border-strong)",
              background: "transparent",
              color: "var(--text-muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.1rem",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ×
          </motion.button>
        </div>

        {/* Content */}
        {isIframe ? (
          <iframe
            src={game.url}
            title={game.title}
            style={{
              flexGrow: 1,
              minHeight: 0,
              border: "none",
              width: "100%",
              display: "block",
            }}
            allow="fullscreen"
          />
        ) : (
          <div style={{
            overflowY: "auto",
            flexGrow: 1,
            padding: "clamp(24px, 5vw, 52px) clamp(20px, 5vw, 56px)",
          }}>
            <AnimatePresence mode="wait">
              {finalScore !== null ? (
                <motion.div
                  key="score"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{ textAlign: "center", padding: "clamp(32px, 8vw, 72px) 0" }}
                >
                  <p style={{
                    fontFamily: "'Space Grotesk Variable', system-ui, sans-serif",
                    fontSize: "clamp(3.5rem, 12vw, 6rem)",
                    fontWeight: 700,
                    letterSpacing: "-0.04em",
                    lineHeight: 1,
                    margin: "0 0 12px",
                    color: "var(--text)",
                  }}>
                    {finalScore}
                    <span style={{ color: "var(--text-muted)" }}>/{game.maxScore}</span>
                  </p>
                  <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: 40 }}>
                    {game.meta}
                  </p>
                  <button className="btn-pill-outline" onClick={onClose}>
                    Back to games
                  </button>
                </motion.div>
              ) : (
                <motion.div key="game" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <GameComponent onDone={onFinish} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */
export default function GamesPage() {
  const [active, setActive]         = useState(null);
  const [scores, setScores]         = useState({});
  const [finalScore, setFinalScore] = useState(null);
  const { darkMode, toggleTheme }   = useTheme();

  const openGame   = useCallback((game) => { setActive(game); setFinalScore(null); }, []);
  const closeGame  = useCallback(() => { setActive(null); setFinalScore(null); }, []);
  const finishGame = useCallback((s) => {
    setScores(prev => ({ ...prev, [active.id]: s }));
    setFinalScore(s);
  }, [active]);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>

      {/* Nav */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0,
        height: "var(--nav-h)",
        zIndex: 1000,
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 clamp(20px, 5vw, 48px)",
      }}>
        <Link to="/" style={{
          fontFamily: "'Space Grotesk Variable', system-ui, sans-serif",
          fontWeight: 700,
          fontSize: "1.2rem",
          color: "var(--text)",
          textDecoration: "none",
          letterSpacing: "-0.03em",
        }}>
          rishi.
        </Link>

        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <Link to="/"
            style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-muted)", textDecoration: "none", letterSpacing: "0.01em", transition: "color 120ms ease" }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--text)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
          >
            ← Portfolio
          </Link>
          <button onClick={toggleTheme} aria-label="Toggle theme" style={{
            width: 36, height: 36, background: "transparent",
            border: "1px solid var(--border-strong)", borderRadius: "50%",
            color: "var(--text-muted)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem",
          }}>
            <i className={`fas ${darkMode ? "fa-sun" : "fa-moon"}`} />
          </button>
        </div>
      </nav>

      {/* Content */}
      <main style={{
        maxWidth: 1100,
        margin: "0 auto",
        paddingTop: "calc(var(--nav-h) + clamp(40px, 8vw, 80px))",
        paddingRight: "clamp(20px, 5vw, 48px)",
        paddingBottom: 120,
        paddingLeft: "clamp(20px, 5vw, 48px)",
      }}>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          style={{ marginBottom: "clamp(40px, 7vw, 72px)" }}
        >
          <h1 style={{
            fontFamily: "'Space Grotesk Variable', system-ui, sans-serif",
            fontSize: "clamp(2rem, 5vw, 3.2rem)",
            fontWeight: 700,
            letterSpacing: "-0.04em",
            lineHeight: 0.93,
            margin: 0,
            color: "var(--text)",
          }}>
            Mini games<br />
            <span style={{ color: "var(--text-muted)" }}>for fun.</span>
          </h1>
        </motion.div>

        {/* Card grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}>
          {GAMES.map((game, i) => (
            <GameCard
              key={game.id}
              game={game}
              onPlay={() => openGame(game)}
              score={scores[game.id]}
              delay={i * 0.06}
            />
          ))}
        </div>
      </main>

      {/* Modal */}
      <AnimatePresence>
        {active && (
          <GameModal
            key={active.id}
            game={active}
            onClose={closeGame}
            onFinish={finishGame}
            finalScore={finalScore}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
